
const ALLERCLIP_REVIEW_TRANSLATION_ENDPOINT =
  'https://allerclip-review-translator.allerclip.workers.dev/translate';

const REVIEW_TEXT_SELECTOR = [
  '.jdgm-cards-carousel p',
  '.jdgm-review-widget .jdgm-rev__title',
  '.jdgm-review-widget .jdgm-rev__body',
  '.jdgm-review-widget [class*="review-title"]',
  '.jdgm-review-widget [class*="review-content"]',
].join(',');

const reviewTranslations = new Map();
const pendingReviewTexts = new Map();
const originalTexts = new WeakMap();

let translationTimer;
let requestInProgress = false;

function reviewKey(text) {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `review-${(hash >>> 0).toString(36)}`;
}

// Manually approved translations for unusually worded reviews.
// The original customer reviews in Judge.me remain unchanged.
function approvedTranslation(text) {
  const value = text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  if (value.includes('ceinturon') &&
      value.includes('plaque')) {
    return 'Much better than wearing a belt. It feels less hot, clips on securely, and the aluminum medical ID plate is a great idea.';
  }

  if (/^très bien mieux que le ceinturons?$/.test(value)) {
    return 'Much better than wearing a belt';
  }

  return null;
}

function getOriginalText(target) {
  if (originalTexts.has(target)) {
    return originalTexts.get(target);
  }

  const original = (
    target.nodeType === Node.TEXT_NODE
      ? target.nodeValue
      : target.textContent
  ).trim();

  if (original) originalTexts.set(target, original);
  return original;
}

function setTranslation(target, translation) {
  if (!target.isConnected) return;

  if (target.nodeType === Node.TEXT_NODE) {
    const raw = target.nodeValue || '';
    const leading = raw.match(/^\s*/)?.[0] || '';
    const trailing = raw.match(/\s*$/)?.[0] || '';
    const updated = leading + translation + trailing;

    if (raw !== updated) target.nodeValue = updated;
  } else if (target.textContent !== translation) {
    target.textContent = translation;
  }
}

function addTranslationTarget(target) {
  const original = getOriginalText(target);
  if (!original || original.length < 8) return;

  const approved = approvedTranslation(original);

  if (approved) {
    reviewTranslations.set(original, approved);
    setTranslation(target, approved);
    return;
  }

  if (reviewTranslations.has(original)) {
    setTranslation(target, reviewTranslations.get(original));
    return;
  }

  let record = pendingReviewTexts.get(original);

  if (!record) {
    record = {
      id: reviewKey(original),
      targets: new Set(),
    };
    pendingReviewTexts.set(original, record);
  }

  record.targets.add(target);
}

// Locate review text inside the Judge.me popup.
// Text nodes are used to preserve the popup's styling and links.
function collectPopupTexts(root) {
  const containers = new Set();

  if (root instanceof Element) {
    if (root.matches('.jm-review-content')) {
      containers.add(root);
    }

    const parent = root.closest('.jm-review-content');
    if (parent) containers.add(parent);
  }

  root.querySelectorAll?.(
    '.jm-review-item .jm-review-content'
  ).forEach((container) => containers.add(container));

  for (const container of containers) {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT
    );

    let node;

    while ((node = walker.nextNode())) {
      const parent = node.parentElement;
      if (!parent) continue;

      // Never translate customer identities, dates,
      // product links or interactive controls.
      if (parent.closest([
        'a',
        'button',
        'time',
        'script',
        'style',
        '[class*="author"]',
        '[class*="reviewer"]',
        '[class*="date"]',
        '[class*="product"]',
      ].join(','))) {
        continue;
      }

      if (node.nodeValue.trim().length >= 8) {
        addTranslationTarget(node);
      }
    }
  }
}

function collectReviewTexts(root = document) {
  if (!document.documentElement.lang
      .toLowerCase().startsWith('en')) {
    return;
  }

  const elements = [];

  if (root instanceof Element &&
      root.matches(REVIEW_TEXT_SELECTOR)) {
    elements.push(root);
  }

  root.querySelectorAll?.(REVIEW_TEXT_SELECTOR)
    .forEach((element) => elements.push(element));

  for (const element of elements) {
    addTranslationTarget(element);
  }

  collectPopupTexts(root);

  if (pendingReviewTexts.size) {
    clearTimeout(translationTimer);
    translationTimer = setTimeout(
      requestReviewTranslations,
      100
    );
  }
}

async function requestReviewTranslations() {
  if (requestInProgress) return;

  const batch = [...pendingReviewTexts.entries()]
    .slice(0, 20);

  if (!batch.length) return;

  requestInProgress = true;

  for (const [text] of batch) {
    pendingReviewTexts.delete(text);
  }

  try {
    const response = await fetch(
      ALLERCLIP_REVIEW_TRANSLATION_ENDPOINT,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          texts: batch.map(([text, record]) => ({
            id: record.id,
            text,
          })),
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();

    const byId = new Map(
      (data.translations || []).map((item) => [
        item.id,
        item.translatedText,
      ])
    );

    for (const [text, record] of batch) {
      const translated = byId.get(record.id);

      if (typeof translated !== 'string' ||
          !translated.trim()) {
        continue;
      }

      const finalText =
        approvedTranslation(text) || translated.trim();

      reviewTranslations.set(text, finalText);

      for (const target of record.targets) {
        setTranslation(target, finalText);
      }
    }
  } catch (error) {
    console.warn(
      'AllerClip review translation unavailable:',
      error
    );
  } finally {
    requestInProgress = false;

    if (pendingReviewTexts.size) {
      setTimeout(requestReviewTranslations, 100);
    }
  }
}

function initializeReviewTranslation() {
  if (!document.documentElement.lang
      .toLowerCase().startsWith('en')) {
    return;
  }

  collectReviewTexts();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          collectReviewTexts(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeReviewTranslation,
    { once: true }
  );
} else {
  initializeReviewTranslation();
}
